import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { View, Text, TouchableOpacity, Modal, Pressable, Animated, ScrollView, ActivityIndicator, Platform, StatusBar, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import Svg, { Polygon } from 'react-native-svg';
import {
  X,
  User,
  ChevronRight,
  ChevronLeft,
  PlusCircle,
  UserPlus,
} from 'lucide-react-native';

import { colors, spacing, typography, animations } from '@/lib/theme';
import { useAuth } from '@/hooks/useAuth';
import { useLeagues, type League as UserLeagueSummary } from '@/hooks/useLeagues';
import { primeLeagueCache } from '@/lib/leagueCache';
import { formatCurrency } from '@/lib/utils';
import {
  getCreateLeagueHref,
  getJoinLeagueHref,
  getLandingHref,
  getLeagueHref,
  getProfileHref,
} from '@/lib/routes';
import WebFeedbackWidget from '@/components/WebFeedbackWidget';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';

const transparentColor = 'transparent';
const navOverlayColor = 'rgba(22, 36, 28, 0.72)';
const mastheadActionRadius = 999;
const mastheadBrandStroke = 1.5;

function MastheadRail({ compact }: { compact: boolean }) {
  const hairlineStyle = [styles.mastheadRailHairline, compact && styles.mastheadRailHairlineCompact];

  return (
    <View style={styles.mastheadRailGroup}>
      <View style={hairlineStyle} />
      <View style={[styles.mastheadRail, compact && styles.mastheadRailCompact]} />
      <View style={hairlineStyle} />
    </View>
  );
}

function MastheadBrandTab({ compact }: { compact: boolean }) {
  const height = compact ? 28 : 36;
  const width = compact ? 88 : 124;

  return (
    <View style={[styles.mastheadBrandTab, compact && styles.mastheadBrandTabCompact]}>
      <Svg
        height={height}
        pointerEvents="none"
        preserveAspectRatio="none"
        viewBox="0 0 100 36"
        width={width}
      >
        <Polygon
          fill={colors.ink.maroon}
          points="14,1.6 86,1.6 98.4,18 86,34.4 14,34.4 1.6,18"
          stroke={colors.ink.paper}
          strokeLinejoin="miter"
          strokeWidth={mastheadBrandStroke}
        />
      </Svg>
      <View style={styles.mastheadBrandTextWrap}>
        <Text
          style={[
            styles.mastheadBrandText,
            compact && styles.mastheadBrandTextCompact,
          ]}
        >
          PCKM
        </Text>
      </View>
    </View>
  );
}

function LeagueMenuPanel({
  children,
  fadeAnim,
  slideAnim,
}: {
  children: ReactNode;
  fadeAnim: Animated.Value;
  slideAnim: Animated.Value;
}) {
  const insets = useSafeAreaInsets();

  return (
    <Animated.View
      style={[
        styles.navMenuContainer,
        {
          opacity: fadeAnim,
          paddingTop: spacing.lg + insets.top,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
}

interface AppHeaderProps {
  title?: string;
  currentLeagueId?: string | null;
  allowBack?: boolean;
  rightAction?: 'profile' | 'close';
}

export default function AppHeader({
  title,
  currentLeagueId,
  allowBack = true,
  rightAction = 'profile',
}: AppHeaderProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const insets = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();
  const { isAuthenticated, user } = useAuth();
  const { data: leagues, isLoading: leaguesLoading } = useLeagues(isAuthenticated);
  const screenName = title ?? 'PCKM';
  const displayTitle = title ?? 'PCKM';
  const canGoBack = allowBack && router.canGoBack();
  const canOpenLeagueMenu = Boolean(currentLeagueId);
  const showMastheadFeedback = Platform.OS === 'web' && (canOpenLeagueMenu || !canGoBack);
  const isCompactMasthead = screenWidth < 600;

  const leagueList: UserLeagueSummary[] = (leagues ?? []).filter(
    (l) => l && l.id && String(l.id) !== 'undefined',
  );
  const isLeagueSelectable = (league: UserLeagueSummary) =>
    league.isActive !== false && league.currentSeason?.isActive !== false;
  const activeLeagueList = leagueList.filter(isLeagueSelectable);
  const inactiveLeagueList = leagueList.filter((league) => !isLeagueSelectable(league));
  const hasLeagues = leagueList.length > 0;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showInactiveLeagues, setShowInactiveLeagues] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(-60)).current;
  const isClosingMenuRef = useRef(false);
  const pendingMenuActionRef = useRef<(() => void) | null>(null);

  const runPendingMenuAction = useCallback(() => {
    const action = pendingMenuActionRef.current;
    pendingMenuActionRef.current = null;
    action?.();
  }, []);

  useEffect(() => {
    if (isMenuOpen || Platform.OS === 'ios' || !pendingMenuActionRef.current) {
      return;
    }

    // Android does not consistently emit Modal.onDismiss. Wait until React has
    // committed visible=false before dispatching navigation so it follows the
    // native modal dismissal command instead of racing it in the same callback.
    const frame = requestAnimationFrame(runPendingMenuAction);
    return () => cancelAnimationFrame(frame);
  }, [isMenuOpen, runPendingMenuAction]);

  const openMenu = () => {
    setIsMenuOpen(true);
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: animations.duration.normal,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        damping: 16,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const closeMenu = (onClosed?: () => void) => {
    if (isClosingMenuRef.current) {
      return;
    }

    // Native Modal stays mounted during the close animation. Wait for it to
    // finish before replacing the route so its backdrop cannot swallow the
    // first interaction on the destination screen.
    isClosingMenuRef.current = true;
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: animations.duration.fast,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -60,
        duration: animations.duration.fast,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      isClosingMenuRef.current = false;
      if (!finished) {
        return;
      }
      pendingMenuActionRef.current = onClosed ?? null;
      setIsMenuOpen(false);
    });
  };

  const closeMenuAndRun = (action: () => void) => {
    closeMenu(action);
  };

  const handleNavigateToProfile = () => {
    router.push(getProfileHref());
  };

  const handleCloseScreen = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace(getLandingHref());
  };

  const handleNavigateToCreateLeague = () => {
    closeMenuAndRun(() => {
      router.push(getCreateLeagueHref());
    });
  };

  const handleNavigateToJoinLeague = () => {
    closeMenuAndRun(() => {
      router.push(getJoinLeagueHref());
    });
  };

  const handleLeagueSwitch = (targetLeagueId: string) => {
    if (!targetLeagueId || targetLeagueId === 'undefined') {
      closeMenu();
      return;
    }

    if (targetLeagueId === currentLeagueId) {
      closeMenu();
      return;
    }

    const targetLeague = leagueList.find(
      (league) => String(league.id) === targetLeagueId,
    );

    if (targetLeague) {
      primeLeagueCache(queryClient, targetLeague);
    }

    const targetSection =
      targetLeague?.currentSeason?.mode === 'march-madness-player-pool' ? 'standings' : 'picks';
    const targetHref = getLeagueHref(targetLeagueId, targetSection, {
      seasonId: targetLeague?.resolvedSeasonId ?? null,
      weekId: null,
    });
    closeMenuAndRun(() => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(String(targetHref));
        return;
      }

      router.replace(targetHref);
    });
  };

  const renderLeagueOption = (leagueOption: UserLeagueSummary, isInactive = false) => {
    const leagueId = String(leagueOption.id);
    const isCurrent = leagueId === currentLeagueId;
    const seasonLabel = leagueOption.currentSeason?.label;
    const weeklyBuyInAmount =
      typeof leagueOption.currentSeason?.weeklyBuyInCents === 'number'
        ? leagueOption.currentSeason.weeklyBuyInCents / 100
        : Number.parseFloat(leagueOption.weeklyBuyIn ?? '0');
    const seasonBuyInAmount =
      typeof leagueOption.currentSeason?.seasonBuyInCents === 'number'
        ? leagueOption.currentSeason.seasonBuyInCents / 100
        : 0;
    const buyInMeta =
      seasonBuyInAmount > 0
        ? `${formatCurrency(seasonBuyInAmount)} season buy-in`
        : weeklyBuyInAmount > 0
          ? `${formatCurrency(weeklyBuyInAmount)} weekly buy-in`
          : 'No description provided';
    const metaText =
      leagueOption.description?.trim() ||
      (seasonLabel ? `Season: ${seasonLabel}` : buyInMeta);

    return (
      <TouchableOpacity
        key={leagueId}
        style={[
          styles.navMenuLeagueItem,
          isCurrent && styles.navMenuLeagueItemActive,
        ]}
        onPress={() => handleLeagueSwitch(leagueId)}
        disabled={isCurrent}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityState={{ disabled: isCurrent }}
      >
        <View style={styles.navMenuLeagueInfo}>
          <Text style={styles.navMenuLeagueName} numberOfLines={1}>
            {leagueOption.name}
          </Text>
          <Text style={styles.navMenuLeagueMeta} numberOfLines={1}>
            {isCurrent
              ? 'Currently viewing this league'
              : isInactive
                ? 'Inactive — open to manage'
                : metaText}
          </Text>
        </View>
        {isCurrent ? (
          <View style={styles.navMenuActiveBadge}>
            <Text style={styles.navMenuActiveBadgeText}>{isInactive ? 'Inactive' : 'Active'}</Text>
          </View>
        ) : (
          <ChevronRight size={18} color={colors.ink.press} />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <Modal
        visible={isMenuOpen}
        transparent
        animationType="none"
        statusBarTranslucent
        onRequestClose={() => closeMenu()}
        onDismiss={runPendingMenuAction}
      >
        <SafeAreaProvider>
          <View style={styles.navMenuOverlay}>
            <Pressable style={styles.navMenuBackdrop} onPress={() => closeMenu()} />
            <LeagueMenuPanel fadeAnim={fadeAnim} slideAnim={slideAnim}>
              <View style={styles.navMenuHeader}>
                <Text style={styles.navMenuTitle}>Switch League</Text>
                <TouchableOpacity
                  onPress={() => closeMenu()}
                  style={styles.navMenuCloseButton}
                  accessibilityRole="button"
                  accessibilityLabel="Close league menu"
                >
                  <X size={18} color={colors.text.primary} />
                </TouchableOpacity>
              </View>

              <View style={styles.navMenuSection}>
                {leaguesLoading ? (
                  <View style={styles.navMenuLoadingState}>
                    <ActivityIndicator color={colors.ink.press} />
                    <Text style={styles.navMenuLoadingText}>Loading leagues...</Text>
                  </View>
                ) : hasLeagues ? (
                  <ScrollView
                    style={styles.navMenuLeagueScroll}
                    contentContainerStyle={styles.navMenuLeagueScrollContent}
                    showsVerticalScrollIndicator={false}
                  >
                    {activeLeagueList.length === 0 && (
                      <Text style={styles.navMenuEmptyText}>No active leagues right now.</Text>
                    )}
                    {activeLeagueList.map((leagueOption) => renderLeagueOption(leagueOption))}
                    {inactiveLeagueList.length > 0 && (
                      <>
                        <TouchableOpacity
                          style={styles.navMenuLeagueItem}
                          onPress={() => setShowInactiveLeagues((visible) => !visible)}
                          activeOpacity={0.8}
                          accessibilityRole="button"
                          accessibilityState={{ expanded: showInactiveLeagues }}
                        >
                          <View style={styles.navMenuLeagueInfo}>
                            <Text style={styles.navMenuLeagueName}>
                              Inactive leagues ({inactiveLeagueList.length})
                            </Text>
                            <Text style={styles.navMenuLeagueMeta}>
                              {showInactiveLeagues ? 'Hide inactive leagues' : 'Show to manage or reactivate'}
                            </Text>
                          </View>
                          <ChevronRight size={18} color={colors.ink.press} />
                        </TouchableOpacity>
                        {showInactiveLeagues && inactiveLeagueList.map((leagueOption) =>
                          renderLeagueOption(leagueOption, true),
                        )}
                      </>
                    )}
                  </ScrollView>
                ) : (
                  <Text style={styles.navMenuEmptyText}>
                    {isAuthenticated
                      ? "You haven't joined any leagues yet."
                      : 'Sign in to view your leagues.'}
                  </Text>
                )}
              </View>

              <TouchableOpacity
                style={styles.navMenuCreateButton}
                onPress={handleNavigateToCreateLeague}
                activeOpacity={0.9}
                accessibilityRole="button"
                accessibilityLabel="Create a new league"
              >
                <View style={styles.navMenuCreateIcon}>
                  <PlusCircle size={20} color={colors.ink.press} />
                </View>
                <View style={styles.navMenuCreateContent}>
                  <Text style={styles.navMenuCreateTitle}>Create a League</Text>
                  <Text style={styles.navMenuCreateSubtitle}>Start a new group in seconds</Text>
                </View>
                <ChevronRight size={18} color={colors.ink.press} />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navMenuJoinButton}
                onPress={handleNavigateToJoinLeague}
                activeOpacity={0.9}
                accessibilityRole="button"
                accessibilityLabel="Join an existing league"
              >
                <View style={styles.navMenuJoinIcon}>
                  <UserPlus size={20} color={colors.ink.press} />
                </View>
                <View style={styles.navMenuJoinContent}>
                  <Text style={styles.navMenuJoinTitle}>Join a League</Text>
                  <Text style={styles.navMenuJoinSubtitle}>Have a code? Enter it here</Text>
                </View>
                <ChevronRight size={18} color={colors.ink.press} />
              </TouchableOpacity>
            </LeagueMenuPanel>
          </View>
        </SafeAreaProvider>
      </Modal>

      <View style={[styles.headerContainer, { paddingTop: insets.top }]}>
        <View
          style={[
            styles.headerContent,
            isCompactMasthead && styles.headerContentCompact,
          ]}
        >
          <View
            style={[
              styles.leadingActions,
              isCompactMasthead && styles.leadingActionsCompact,
            ]}
          >
            {showMastheadFeedback ? (
              <WebFeedbackWidget variant="masthead" compact={isCompactMasthead} />
            ) : canGoBack ? (
              <TouchableOpacity
                style={[
                  styles.mastheadActionButton,
                  isCompactMasthead && styles.mastheadActionButtonCompact,
                ]}
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel={`Go back from ${screenName}`}
                activeOpacity={0.8}
              >
                <ChevronLeft size={24} color={colors.text.inverse} />
              </TouchableOpacity>
            ) : (
              <View
                style={[
                  styles.mastheadActionButton,
                  isCompactMasthead && styles.mastheadActionButtonCompact,
                  styles.mastheadActionPlaceholder,
                ]}
              />
            )}
          </View>
          <MastheadRail compact={isCompactMasthead} />

          <View style={styles.titleContainer}>
            {canOpenLeagueMenu ? (
              <TouchableOpacity
                style={[
                  styles.mastheadIdentity,
                  isCompactMasthead && styles.mastheadIdentityCompact,
                ]}
                onPress={openMenu}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel="Switch League"
              >
                <MastheadBrandTab compact={isCompactMasthead} />
                <Text
                  style={[
                    styles.mastheadTitleText,
                    isCompactMasthead && styles.mastheadTitleTextCompact,
                  ]}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.65}
                >
                  {displayTitle}
                </Text>
              </TouchableOpacity>
            ) : (
              <View
                style={[
                  styles.mastheadIdentity,
                  isCompactMasthead && styles.mastheadIdentityCompact,
                ]}
              >
                <MastheadBrandTab compact={isCompactMasthead} />
                <Text
                  style={[
                    styles.mastheadTitleText,
                    isCompactMasthead && styles.mastheadTitleTextCompact,
                  ]}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.65}
                >
                  {displayTitle}
                </Text>
              </View>
            )}
          </View>

          <MastheadRail compact={isCompactMasthead} />
          <View
            style={[
              styles.trailingActions,
              isCompactMasthead && styles.trailingActionsCompact,
            ]}
          >
            <TouchableOpacity
              style={[
                rightAction === 'close' && styles.mastheadActionButton,
                rightAction === 'close' && isCompactMasthead && styles.mastheadActionButtonCompact,
                rightAction !== 'close' && styles.mastheadProfileHitbox,
              ]}
              onPress={rightAction === 'close' ? handleCloseScreen : handleNavigateToProfile}
              accessibilityRole="button"
              accessibilityLabel={rightAction === 'close' ? 'Close screen' : 'Profile settings'}
              activeOpacity={0.8}
            >
              {rightAction === 'close' ? (
                <X size={22} color={colors.text.inverse} />
              ) : (
                <Avatar
                  size={isCompactMasthead ? 40 : 56}
                  borderColor="paper"
                  borderWidth={2}
                  style={[
                    styles.mastheadProfileAvatar,
                    isCompactMasthead && styles.mastheadProfileAvatarCompact,
                  ]}
                >
                  {user?.profileImageUrl ? <AvatarImage src={user.profileImageUrl} /> : null}
                  <AvatarFallback>
                    <User size={18} color={colors.text.inverse} />
                  </AvatarFallback>
                </Avatar>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create(() => ({
  headerContainer: {
    backgroundColor: colors.ink.field,
    borderBottomWidth: 0,
    elevation: 0,
    position: 'relative',
    shadowColor: transparentColor,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    zIndex: 10,
  },
  headerContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 0,
    height: 108,
    justifyContent: 'space-between',
    overflow: 'hidden',
    paddingHorizontal: 0,
    width: '100%',
  },
  headerContentCompact: {
    height: 72,
  },
  leadingActions: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: 180,
    minWidth: 72,
    width: '18%',
  },
  leadingActionsCompact: {
    minWidth: 56,
    width: '16%',
  },
  mastheadActionButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.fieldDeep,
    borderRadius: mastheadActionRadius,
    height: 56,
    justifyContent: 'center',
    width: 56,
  },
  mastheadActionButtonCompact: {
    height: 40,
    width: 40,
  },
  mastheadActionPlaceholder: {
    backgroundColor: transparentColor,
  },
  mastheadBrandTab: {
    alignItems: 'center',
    height: 36,
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    width: 124,
  },
  mastheadBrandTabCompact: {
    height: 28,
    width: 88,
  },
  mastheadBrandText: {
    color: colors.text.inverse,
    fontFamily: typography.fontFamily.display,
    fontSize: typography.fontSize.xl,
    includeFontPadding: false,
    letterSpacing: 1.5,
    lineHeight: 22,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  mastheadBrandTextCompact: {
    fontSize: typography.fontSize.base,
    letterSpacing: 1,
    lineHeight: 16,
  },
  mastheadBrandTextWrap: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    paddingTop: 2,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  mastheadIdentity: {
    alignItems: 'center',
    alignSelf: 'center',
    gap: spacing.xs,
    justifyContent: 'center',
    maxWidth: '100%',
    paddingHorizontal: spacing.md,
  },
  mastheadIdentityCompact: {
    gap: 2,
    paddingHorizontal: spacing.sm,
  },
  mastheadProfileAvatar: {
    backgroundColor: colors.ink.fieldDeep,
    borderColor: colors.ink.paper,
    borderRadius: mastheadActionRadius,
    borderWidth: 2,
    height: 56,
    overflow: 'hidden',
    width: 56,
  },
  mastheadProfileAvatarCompact: {
    height: 40,
    width: 40,
  },
  mastheadProfileHitbox: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  mastheadRail: {
    alignSelf: 'stretch',
    backgroundColor: colors.ink.maroon,
    width: 28,
  },
  mastheadRailCompact: {
    width: 12,
  },
  mastheadRailGroup: {
    alignSelf: 'stretch',
    flexDirection: 'row',
  },
  mastheadRailHairline: {
    alignSelf: 'stretch',
    backgroundColor: colors.ink.paper,
    width: 6,
  },
  mastheadRailHairlineCompact: {
    width: 3,
  },
  mastheadTitleText: {
    color: colors.text.inverse,
    flexShrink: 1,
    fontFamily: typography.fontFamily.display,
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.extrabold,
    letterSpacing: -1,
    minWidth: 0,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  mastheadTitleTextCompact: {
    fontSize: typography.fontSize.xl,
    letterSpacing: -0.5,
  },
  navMenuActiveBadge: {
    backgroundColor: colors.success[100],
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  navMenuActiveBadgeText: {
    color: colors.ink.press,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase',
  },
  navMenuBackdrop: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  navMenuCloseButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  navMenuContainer: {
    alignSelf: 'center',
    backgroundColor: colors.ink.paper,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    maxWidth: 400,
    paddingBottom: spacing.base,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    shadowColor: transparentColor,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    width: 'auto',
    zIndex: 1,
  },
  navMenuCreateButton: {
    alignItems: 'center',
    backgroundColor: colors.state.successBg,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.md,
    padding: spacing.md,
  },
  navMenuCreateContent: {
    flex: 1,
  },
  navMenuCreateIcon: {
    alignItems: 'center',
    backgroundColor: colors.ink.paper,
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  navMenuCreateSubtitle: {
    color: colors.ink.pressMuted,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs / 2,
  },
  navMenuCreateTitle: {
    color: colors.ink.press,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase',
  },
  navMenuEmptyText: {
    color: colors.text.secondary,
    fontSize: typography.fontSize.sm,
  },
  navMenuHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  navMenuJoinButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.paper,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.lg,
    padding: spacing.md,
  },
  navMenuJoinContent: {
    flex: 1,
  },
  navMenuJoinIcon: {
    alignItems: 'center',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.cardBorder,
    borderRadius: 999,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  navMenuJoinSubtitle: {
    color: colors.ink.pressMuted,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs / 2,
  },
  navMenuJoinTitle: {
    color: colors.ink.press,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase',
  },
  navMenuLeagueInfo: {
    flex: 1,
    marginRight: spacing.sm,
  },
  navMenuLeagueItem: {
    alignItems: 'center',
    backgroundColor: colors.ink.paper,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  navMenuLeagueItemActive: {
    backgroundColor: colors.state.successBg,
    borderColor: colors.state.successBorder,
  },
  navMenuLeagueMeta: {
    color: colors.ink.pressMuted,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs / 2,
  },
  navMenuLeagueName: {
    color: colors.ink.press,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  navMenuLeagueScroll: {
    maxHeight: 240,
  },
  navMenuLeagueScrollContent: {
    paddingBottom: spacing.sm,
  },
  navMenuLoadingState: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  navMenuLoadingText: {
    color: colors.ink.pressMuted,
    fontSize: typography.fontSize.sm,
  },
  navMenuOverlay: {
    alignItems: 'center',
    backgroundColor: navOverlayColor,
    flex: 1,
    justifyContent: 'flex-start',
  },
  navMenuSection: {
    marginBottom: spacing.lg,
  },
  navMenuTitle: {
    color: colors.ink.press,
    fontFamily: typography.fontFamily.display,
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.extrabold,
    letterSpacing: -0.4,
    textTransform: 'uppercase',
  },
  titleContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: 0,
    maxWidth: '100%',
    minWidth: 0,
  },
  trailingActions: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    maxWidth: 180,
    minWidth: 72,
    width: '18%',
  },
  trailingActionsCompact: {
    minWidth: 56,
    width: '16%',
  },
}));
